import { Component, OnInit, OnDestroy, EventEmitter, Renderer2, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { AlertsService } from '../../services/alerts.service';
import { CartService, ItemsCart } from '../services/cart.service';
import { UtilsService } from '../../services/utils.service';
import { BlockUIWrapperComponent } from '../../layout/block-ui/block-ui-wrapper.component';
import { CartTransactionService, PrettyCartTransaction } from '../services/cart-transaction.service';
import { Subscription } from 'rxjs';


@Component({
  selector: 'cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.css']
})
export class CartComponent implements OnInit, OnDestroy {
  user: any;
  cartOn = false;
  cartCount = 0;
  totalAmount = 0;
  cart: { [key: string]: ItemsCart } = {};
  permissions: any = {};
  redirecting = false;
  isOpen = false;
  symbol: string = '$';
  fractionSize: number = 2;
  expandedKeys: string | null = null;
  @ViewChild('blockUIWrapper') blockUIWrapper!: BlockUIWrapperComponent;
  //@ViewChild('blockUIWrapper', { static: false }) blockUIWrapper!: BlockUIWrapperComponent;
  private userPrefsUpdateSubscription: Subscription = new Subscription();
  private cartUpdatedSubscription: Subscription = new Subscription();
  
  constructor(
    private cartService: CartService,
    private userService: UserService,
    private transactionService: CartTransactionService,
    private alertsService: AlertsService,
    private route: ActivatedRoute,
	private utilsService: UtilsService,
    private router: Router,
    private renderer: Renderer2
  ) {
  }

  ngOnInit(): void {
    this.user = this.userService.user;
    this.updateCart();
    this.updatePermissions();
    
    // Subscribe to user preferences update
    this.userPrefsUpdateSubscription = this.userService.broadcastEvent.subscribe((eventId) => {
      if("userPrefsUpdate" === eventId){
      	this.updatePermissions();
      }
    });

    // Subscribe to cart updates
    this.cartUpdatedSubscription = this.cartService.broadcastEvent.subscribe((eventId) => {
      if("cartUpdated" === eventId){
      	this.updateCart();
      } 
      /*else if("expandedKeysUpdated" === eventId){
		  this.expandedKeys = this.cartService.getExpandedKeys();
	  }*/
    });

    // Initial permission setup
    this.updatePermissions();

    // Check for transaction on page load
    const transaction = this.route.snapshot.paramMap.get('transaction');
    if (transaction) {
      this.cancelOrder();
    }
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    if (this.userPrefsUpdateSubscription) {
      this.userPrefsUpdateSubscription.unsubscribe();
    }

    if (this.cartUpdatedSubscription) {
      this.cartUpdatedSubscription.unsubscribe();
    }
  }

  updatePermissions(): void {
    if (this.user.permissions) {
      this.permissions = this.user.permissions.reduce((map: { [key: string]: number }, obj: string) => {
        map[obj] = 1;
        return map;
      }, {});
    }

    this.cartOn = !this.permissions['ROLE_PRODUCT_DOWNLOAD'];
  }

  updateCart(): void {
    this.cartCount = this.cartService.count;
    this.cart = this.cartService.itemsCart;
    this.totalAmount = this.cartService.totalAmount;
  }

  showDocument(id: string): void {
    this.cartService.showDocument(id);
  }

  showPage(key: string | undefined): void {
	  this.cartService.showPage(key);
  }

  checkoutCart(proceed: boolean): void {
    this.isOpen = true;
    const documents = Object.values(this.cartService.cart);

    if (this.user.loggedin || (!this.user.loggedin && proceed)) {
      if (this.permissions['ROLE_TRANSACTION_CHECKOUT_PAYPAL']) {
        this.redirecting = true;
        this.blockUIWrapper.start('Redirecting to PayPal...');
        this.transactionService.createPayPalPayment(documents).then(data => {
          if (!data.includes('http')) {
            //then total is zero and we received transaction id;
            this.transactionService.submitTransaction(documents, false, data).then((newPurchase: PrettyCartTransaction) => {
              this.blockUIWrapper.message('Placing order...');
              setTimeout(() => {
                this.cartService.clearCart();
                this.router.navigate(['checkout-complete'], { 
				  queryParams: { executed: true, newPurchase: JSON.stringify(newPurchase), kiosk: false }
				});


                this.blockUIWrapper.stop();
              }, 1000);
            }, () => {
              this.blockUIWrapper.stop();
              this.alertsService.error({ message: 'Failed to checkout.' });
            });
          } else {
            // Redirect to PayPal
            // Create form element
		    const form = this.renderer.createElement('form');
		    // Set attributes
		    this.renderer.setAttribute(form, 'class', 'hidden');
		    this.renderer.setAttribute(form, 'action', `${data}&TEMPLATE=TEMPLATEA`);
		    this.renderer.setAttribute(form, 'method', 'POST');
		
		    // Append form to body
		    const body = this.renderer.selectRootElement('body', true);
		    this.renderer.appendChild(body, form);
            form.submit();
          }
        }, () => {
          this.redirecting = false;
          this.blockUIWrapper.stop();
          this.alertsService.error({ title: 'Cannot Checkout', message: 'Please try again later.' });
        });
      } else {
        // For kiosk users
        this.redirecting = true;
        this.blockUIWrapper.start('Placing order...');
        this.transactionService.submitTransaction(documents, true, null).then((newPurchase: PrettyCartTransaction) => {
          setTimeout(() => {
            this.cartService.clearCart();
            this.router.navigate(['checkout-complete'], { 
			  queryParams: { executed: true, newPurchase: JSON.stringify(newPurchase), kiosk: true }
			});

            this.blockUIWrapper.stop();
          }, 1000);
        }, () => {
          this.redirecting = false;
          this.blockUIWrapper.stop();
          this.alertsService.error({ title: 'Cannot Checkout', message: 'Please try again later.' });
        });
      }
    }
  }

  clearCart(): void {
    this.cartService.clearCart();
  }

  removeDocument(id: string): void {
    this.cartService.removeDocument(id);
  }

  removePage(id: string | undefined): void {
	if(id){
    	this.cartService.removePage(id);
    }
  }

  cancelOrder(): void {
    const tr = this.route.snapshot.paramMap.get('transaction');
    this.router.navigate([], { queryParams: {} });
    this.blockUIWrapper.start('Cancelling order...');
    if(tr){
	    this.transactionService.cancelTransaction(tr).then(() => {
	      this.blockUIWrapper.stop();
	    }, () => {
	      this.blockUIWrapper.stop();
	    });
    } else {
		console.error('empty transaction');
		this.blockUIWrapper.stop();	
	}
  }

  register(): void {
    this.userService.runBroadcastEvent('register');
  }

  login(): void {
    this.userService.runBroadcastEvent('login');
  }
  
  setExpandedKeys(expandedKeys: unknown): void {
	  this.expandedKeys = this.utilsService.isString(expandedKeys) ? expandedKeys as string : '';
  }
  
  getFormattedString(value: unknown): string {
    return this.utilsService.getFormattedObject<string>(value, 'string');
  }
  
  getFormattedArray(value: unknown): any[] {
    return this.utilsService.getFormattedObject<any[]>(value, 'array');
  }
}
